import React, { useEffect, useState, useCallback } from 'react';
import { Upload, Button, Image, Modal, message, Tooltip, Progress, type UploadFile, type UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { PlusOutlined, UploadOutlined, FileImageOutlined, PlayCircleOutlined, FileOutlined } from '@ant-design/icons';
import ImgCrop from 'antd-img-crop';
import { FileIcon, defaultStyles } from 'react-file-icon';
import { generateUploadUrl, uploadFile, batchGetFilesByUrl } from '@/services/admin/file';

export interface ProFileUploadProps {
  /** 受控值：文件URL列表 */
  value?: string[];
  /** 值变化回调 */
  onChange?: (urls: string[]) => void;
  /** 初始文件URL列表（用于回显，仅在组件初始化时使用） */
  initialUrls?: string[];
  /** 初始文件对象列表（用于预加载文件，立即显示并可选择自动上传） */
  initialFiles?: File[];
  /** 是否自动上传（默认 true，保持向前兼容）。设置为 false 时需要手动调用 uploadTriggerRef 触发上传 */
  autoUpload?: boolean;
  /** 业务类型：image/document/video/audio/other */
  businessType: 'image' | 'document' | 'video' | 'audio' | 'other';
  /** 动态获取业务类型的回调函数（根据文件类型返回对应的 businessType） */
  getBusinessType?: (file: RcFile) => 'image' | 'document' | 'video' | 'audio' | 'other';
  /** 最大文件数量 */
  maxCount?: number;
  /** 文件展示类型：text/picture-list/picture-card (picture 已废弃，请使用 picture-list) */
  listType?: 'text' | 'picture' | 'picture-list' | 'picture-card';
  /** 上传按钮配置 */
  buttonConfig?: {
    /** 按钮文本 */
    text?: string;
    /** 按钮图标 */
    icon?: React.ReactNode;
    /** 按钮类型 */
    type?: 'default' | 'primary' | 'dashed' | 'link' | 'text';
    /** 按钮大小 */
    size?: 'small' | 'middle' | 'large';
    /** 自定义按钮样式 */
    style?: React.CSSProperties;
    /** 自定义按钮类名 */
    className?: string;
  };
  /** 文件类型限制（MIME类型，如：image/jpeg,image/png）。如果提供了 allowedExtensions，会自动生成 */
  accept?: string;
  /** 文件扩展名限制（如：['jpg', 'jpeg', 'png', 'pdf']）。推荐使用此参数，会自动生成 accept 和处理校验 */
  allowedExtensions?: string[];
  /** 最大文件大小（MB） */
  maxSize?: number;
  /** 图片裁剪配置（仅对图片类型有效） */
  cropConfig?: {
    /** 是否启用裁剪 */
    enabled?: boolean;
    /** 裁剪比例（如 1 表示1:1） */
    aspect?: number;
    /** 图片质量（0-1） */
    quality?: number;
    /** 是否允许缩放 */
    zoom?: boolean;
    /** 是否允许旋转 */
    rotate?: boolean;
    /** 裁剪弹窗标题 */
    modalTitle?: string;
    /** 确定按钮文本 */
    modalOk?: string;
    /** 取消按钮文本 */
    modalCancel?: string;
  };
  /** 是否显示预览功能 */
  showPreview?: boolean;
  /** 是否显示删除功能 */
  showRemove?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 自定义预览处理 */
  onPreview?: (file: UploadFile) => void;
  /** 自定义删除处理 */
  onRemove?: (file: UploadFile) => boolean | undefined;
  /** 上传成功回调 */
  onUploadSuccess?: (file: UploadFile, fileUrl: string) => void;
  /** 上传失败回调 */
  onUploadError?: (file: UploadFile, error: Error) => void;
  /** 上传进度回调 */
  onUploadProgress?: (file: UploadFile, percent: number) => void;
  /** 自定义前缀（用于文件路径） */
  customPrefix?: string | null;
  /** 是否自动获取文件信息（批量），默认为 true */
  autoFetchFileInfo?: boolean;
  /** 文件信息获取成功回调 */
  onFileInfoFetched?: (fileInfos: (API.FileRecordResponse | null)[]) => void;
  /** 额外的上传配置 */
  uploadProps?: Partial<UploadProps>;
  /** 提示文本 */
  extra?: React.ReactNode;
  /** 工具提示 */
  tooltip?: string;
  /** 组件模式：upload（上传模式，可编辑）| preview（预览模式，只读） */
  mode?: 'upload' | 'preview';
  /** 手动上传触发器引用（当 autoUpload=false 时使用） */
  uploadTriggerRef?: React.MutableRefObject<{
    /** 触发上传所有待上传的文件 */
    uploadAll: () => Promise<{ success: string[]; failed: Array<{ file: File; error: Error }> }>;
    /** 触发上传指定的文件 */
    uploadFile: (file: File) => Promise<string>;
  } | null>;
}

/**
 * 判断文件是否为图片
 */
const isImageFile = (file: UploadFile | RcFile): boolean => {
  const url = (file as UploadFile).url || '';
  const name = file.name || '';
  const type = (file as any).type || '';
  
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
  const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp', 'image/svg+xml'];
  
  const ext = (url || name).split('.').pop()?.toLowerCase() || '';
  return imageExtensions.includes(ext) || imageTypes.some(t => type.includes(t));
};

/**
 * 判断文件是否为视频
 */
const isVideoFile = (file: UploadFile | RcFile): boolean => {
  const url = (file as UploadFile).url || '';
  const name = file.name || '';
  const type = (file as any).type || '';
  
  const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];
  const videoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv', 'video/x-flv', 'video/webm'];
  
  const ext = (url || name).split('.').pop()?.toLowerCase() || '';
  return videoExtensions.includes(ext) || videoTypes.some(t => type.includes(t));
};

/**
 * 获取文件扩展名
 */
const getFileExtension = (file: UploadFile | RcFile): string => {
  const url = (file as UploadFile).url || '';
  const name = file.name || '';
  return (url || name).split('.').pop()?.toLowerCase() || '';
};

/**
 * 格式化文件大小
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / k ** i).toFixed(2)} ${sizes[i]}`;
};

/**
 * 文件名中间省略
 */
const truncateFileName = (fileName: string, maxLength: number = 40): string => {
  if (fileName.length <= maxLength) return fileName;
  
  // 分离文件名和扩展名
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) {
    // 没有扩展名，直接中间省略
    const frontLength = Math.floor((maxLength - 3) / 2);
    const backLength = maxLength - 3 - frontLength;
    return `${fileName.slice(0, frontLength)}...${fileName.slice(-backLength)}`;
  }
  
  const nameWithoutExt = fileName.slice(0, lastDotIndex);
  const extension = fileName.slice(lastDotIndex + 1);
  
  // 如果扩展名太长，直接截断
  if (extension.length > 8) {
    return `${fileName.slice(0, maxLength - 3)}...`;
  }
  
  // 计算可用于文件名的长度（减去扩展名、点和省略号）
  const maxNameLength = maxLength - extension.length - 4; // 4 for "... ."
  
  if (nameWithoutExt.length <= maxNameLength) {
    return fileName;
  }
  
  // 中间省略：前面保留一部分，后面保留一部分
  const frontLength = Math.floor(maxNameLength / 2);
  const backLength = maxNameLength - frontLength;
  
  return `${nameWithoutExt.slice(0, frontLength)}...${nameWithoutExt.slice(-backLength)}.${extension}`;
};

/**
 * 生成视频缩略图
 */
const generateVideoThumbnail = (videoUrl: string): Promise<string | null> => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true; // 静音以避免自动播放策略限制
    
    let resolved = false;
    
    const cleanup = () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      video.src = '';
      video.load();
    };
    
    const onLoadedMetadata = () => {
      // 设置视频到第一帧
      video.currentTime = 0.1; // 稍微延迟一点确保有画面
    };
    
    const onSeeked = () => {
      if (resolved) return;
      resolved = true;
      
      try {
        const canvas = document.createElement('canvas');
        const width = video.videoWidth || 320;
        const height = video.videoHeight || 240;
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
          ctx.drawImage(video, 0, 0, width, height);
          const thumbnail = canvas.toDataURL('image/jpeg', 0.8);
          cleanup();
          resolve(thumbnail);
        } else {
          cleanup();
          resolve(null);
        }
      } catch (error) {
        console.error('生成视频缩略图失败:', error);
        cleanup();
        resolve(null);
      }
    };
    
    const onError = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve(null);
    };
    
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    
    // 设置超时，避免长时间等待
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(null);
      }
    }, 5000);
    
    video.src = videoUrl;
    video.load();
  });
};

/**
 * 根据文件扩展名自动生成 accept 字符串
 */
const generateAcceptFromExtensions = (extensions: string[]): string => {
  // MIME 类型映射表
  const mimeMap: Record<string, string> = {
    // 图片
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    svg: 'image/svg+xml',
    // 文档
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    csv: 'text/csv',
    txt: 'text/plain',
    md: 'text/markdown',
    markdown: 'text/markdown',
    mdx: 'text/markdown',
    html: 'text/html',
    // 视频
    mp4: 'video/mp4',
    avi: 'video/x-msvideo',
    mov: 'video/quicktime',
    wmv: 'video/x-ms-wmv',
    flv: 'video/x-flv',
    webm: 'video/webm',
    mkv: 'video/x-matroska',
    // 音频
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    m4a: 'audio/mp4',
  };
  
  const accepts = extensions.map(ext => {
    const lowerExt = ext.toLowerCase();
    const mime = mimeMap[lowerExt];
    // 同时添加 MIME 类型和扩展名格式，提高兼容性
    return mime ? `${mime},.${ext}` : `.${ext}`;
  });
  
  return accepts.join(',');
};

/**
 * 文件类型验证
 */
const validateFileType = (
  file: RcFile,
  accept?: string,
  allowedExtensions?: string[],
): boolean => {
  if (!accept && !allowedExtensions) {
    return true; // 没有限制，允许所有类型
  }

  const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
  const fileType = file.type || '';

  // 优先检查扩展名（更可靠）
  if (allowedExtensions && allowedExtensions.length > 0) {
    return allowedExtensions.includes(fileExtension);
  }

  // 如果没有 allowedExtensions，检查 accept（MIME 类型或扩展名）
  if (accept) {
    const acceptedTypes = accept.split(',').map(t => t.trim());
    
    // 检查是否匹配扩展名格式（如 .pdf）
    const extensionMatch = acceptedTypes.some((type) => {
      if (type.startsWith('.')) {
        return type.toLowerCase() === `.${fileExtension}`;
      }
      return false;
    });
    
    if (extensionMatch) {
      return true;
    }
    
    // 检查 MIME 类型
    if (fileType) {
      const mimeMatch = acceptedTypes.some((type) => {
        if (type.startsWith('.')) {
          return false; // 跳过扩展名格式
        }
        if (type.endsWith('/*')) {
          return fileType.startsWith(type.slice(0, -1));
        }
        return fileType === type;
      });
      
      if (mimeMatch) {
        return true;
      }
    }
    
    // 如果都不匹配，返回 false
    return false;
  }

  return true;
};

/**
 * 通用文件上传组件
 * 
 * 功能特性：
 * - 支持文件选择、上传、预览、删除
 * - 自动处理文件上传（直传+降级方案）
 * - 支持图片裁剪（1:1等比例）
 * - 支持批量获取文件信息
 * - 图片/视频自动显示预览图，文件显示图标
 * - 支持多种展示样式（列表/宫格）
 * - 支持预览模式（只读，用于详情页查看）
 */
const ProFileUpload: React.FC<ProFileUploadProps> = ({
  value,
  onChange,
  initialUrls = [],
  initialFiles = [],
  autoUpload = true,
  businessType,
  getBusinessType,
  maxCount = 1,
  listType = 'picture-card',
  buttonConfig = {},
  accept,
  allowedExtensions,
  maxSize,
  cropConfig,
  showPreview = true,
  showRemove = true,
  disabled = false,
  onPreview: customOnPreview,
  onRemove: customOnRemove,
  onUploadSuccess,
  onUploadError,
  onUploadProgress,
  customPrefix,
  autoFetchFileInfo = true,
  onFileInfoFetched,
  uploadProps = {},
  extra,
  tooltip: _tooltip,
  mode = 'upload',
  uploadTriggerRef,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewVideo, setPreviewVideo] = useState<string>('');
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false);
  const [_isLoadingFileInfo, setIsLoadingFileInfo] = useState<boolean>(false);
  // 存储文件信息（用于显示文件大小等）
  const [fileInfoMap, setFileInfoMap] = useState<Map<string, { size?: number; originalName?: string }>>(new Map());
  // 存储待上传的文件（当 autoUpload=false 时）
  const [pendingUploads, setPendingUploads] = useState<Map<string, RcFile>>(new Map());
  // 标记是否已通过 initialFiles/initialUrls 初始化过
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  // 记录是否有正在上传的文件
  const hasUploading = fileList.some(f => f.status === 'uploading');
  
  // 规范化 listType（向后兼容：picture -> picture-list）
  const normalizedListType = listType === 'picture' ? 'picture-list' : (listType || 'picture-card');
  
  // 自动生成 accept（如果提供了 allowedExtensions）
  const finalAccept = accept || (allowedExtensions && allowedExtensions.length > 0 ? generateAcceptFromExtensions(allowedExtensions) : undefined);

  // 从URL列表初始化文件列表
  useEffect(() => {
    if (initialUrls && initialUrls.length > 0 && fileList.length === 0 && !isInitialized) {
      const initFiles: UploadFile[] = initialUrls.map((url, index) => ({
        uid: `init-${index}-${url}`,
        name: `文件${index + 1}`,
        status: 'done',
        url,
      }));
      setFileList(initFiles);
      setIsInitialized(true);
      
      // 自动获取文件信息（默认启用，除非明确禁用）
      if (autoFetchFileInfo !== false) {
        fetchFileInfos(initialUrls);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrls]);

  // 从 File 对象列表初始化文件列表
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0 && fileList.length === 0 && !isInitialized) {
      const initFiles: UploadFile[] = initialFiles.map((file, index) => {
        const uid = `file-${Date.now()}-${index}-${file.name}`;
        const rcFile = file as RcFile;
        rcFile.uid = uid;
        
        return {
          uid,
          name: file.name,
          status: autoUpload ? 'done' : undefined, // autoUpload=false 时不设置状态（待上传）
          originFileObj: rcFile,
          size: file.size,
        };
      });
      
      setFileList(initFiles);
      setIsInitialized(true);
      
      // 如果启用自动上传，立即触发上传
      if (autoUpload) {
        initFiles.forEach((uploadFile) => {
          const file = uploadFile.originFileObj;
          if (file) {
            // 更新状态为 uploading
            setFileList(prev => prev.map(f => 
              f.uid === uploadFile.uid ? { ...f, status: 'uploading', percent: 0 } : f
            ));
            
            // 触发上传
            const mockOptions = {
              file,
              onSuccess: () => {},
              onError: () => {},
              onProgress: () => {},
            };
            createUploadHandler(setFileList)(mockOptions);
          }
        });
      } else {
        // 不自动上传，将文件加入待上传队列
        const newPendingUploads = new Map<string, RcFile>();
        initFiles.forEach((uploadFile) => {
          if (uploadFile.originFileObj) {
            newPendingUploads.set(uploadFile.uid, uploadFile.originFileObj);
          }
        });
        setPendingUploads(newPendingUploads);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiles]);

  // 同步受控值到文件列表
  useEffect(() => {
    // 如果已通过 initialFiles/initialUrls 初始化，不再接受 value 的空数组覆盖
    if (value !== undefined) {
      const currentUrls = fileList.map(f => f.url).filter((url): url is string => !!url);
      const valueChanged = JSON.stringify(currentUrls) !== JSON.stringify(value);
      
      // 关键优化：如果已通过 initialFiles/initialUrls 初始化
      if (isInitialized) {
        // 1. 忽略 value 从空变空的情况
        if (value.length === 0 && currentUrls.length === 0) {
          return;
        }
        // 2. 忽略 value 为空但当前有文件的情况（说明正在上传或已有文件）
        if (value.length === 0 && fileList.length > 0) {
          return;
        }
        // 3. 只在 value 有 URL 且确实变化时才同步
        if (valueChanged && value.length > 0 && !hasUploading) {
          // 合并状态：保留正在上传的文件，只更新已完成的
          setFileList(prev => {
            // 找出还在上传的文件
            const uploadingFiles = prev.filter(f => f.status === 'uploading' || f.status === 'error');
            
            // 从 value 创建新的已完成文件列表
            const doneFiles: UploadFile[] = value.map((url, index) => {
              // 检查是否已存在（避免重复）
              const existing = prev.find(f => f.url === url);
              if (existing) {
                return existing;
              }
              return {
                uid: `value-${index}-${url}`,
                name: `文件${index + 1}`,
                status: 'done' as const,
                url,
              };
            });
            
            // 合并：保留上传中的 + 新的已完成的
            return [...uploadingFiles, ...doneFiles];
          });
          
          // 自动获取文件信息（默认启用，除非明确禁用）
          if (autoFetchFileInfo !== false && value.length > 0) {
            fetchFileInfos(value);
          }
        }
      } else {
        // 未初始化时，按原逻辑处理（向前兼容）
        if (valueChanged && value.length > 0) {
          const files: UploadFile[] = value.map((url, index) => ({
            uid: `value-${index}-${url}`,
            name: `文件${index + 1}`,
            status: 'done' as const,
            url,
          }));
          setFileList(files);
          setIsInitialized(true);
          
          // 自动获取文件信息（默认启用，除非明确禁用）
          if (autoFetchFileInfo !== false) {
            fetchFileInfos(value);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, hasUploading]);

  // 批量获取文件信息
  const fetchFileInfos = useCallback(async (urls: string[]) => {
    if (urls.length === 0) return;
    
    setIsLoadingFileInfo(true);
    try {
      const response = await batchGetFilesByUrl(urls);
      if (response.code === 0 && response.data) {
        // 更新文件列表中的文件名等信息
        setFileList((prev) => {
          const newList = prev.map((file) => {
            const url = file.url;
            if (!url) return file;
            
            // 按顺序匹配：response.data.items 的顺序对应 urls 的顺序
            const urlIndex = urls.indexOf(url);
            const fileInfo = urlIndex >= 0 ? response.data?.items?.[urlIndex] : null;
            
            if (fileInfo) {
              const updatedFile: UploadFile = {
                ...file,
                name: fileInfo.original_filename || file.name,
              };
              
              // 保存文件信息（大小、原始文件名等）
              setFileInfoMap((prev) => {
                const newMap = new Map(prev);
                newMap.set(url, {
                  size: fileInfo.file_size,
                  originalName: fileInfo.original_filename,
                });
                return newMap;
              });
              
              // 如果是视频文件，生成预览缩略图
              if (isVideoFile(updatedFile)) {
                // 异步生成缩略图，不阻塞文件列表更新
                generateVideoThumbnail(url).then((thumbnail) => {
                  if (thumbnail) {
                    setFileList((current) =>
                      current.map((f) => {
                        if (f.uid === updatedFile.uid) {
                          return { ...f, thumbUrl: thumbnail };
                        }
                        return f;
                      })
                    );
                  }
                }).catch((error) => {
                  console.error('生成视频缩略图失败:', error);
                });
              }
              
              return updatedFile;
            }
            return file;
          });
          
          // 触发 onChange（如果文件名有变化）
          const newUrls = newList.map(f => f.url).filter((url): url is string => !!url);
          if (onChange && JSON.stringify(newUrls) !== JSON.stringify(urls)) {
            onChange(newUrls);
          }
          
          return newList;
        });
        
        onFileInfoFetched?.(response.data.items);
      }
    } catch (error) {
      console.error('获取文件信息失败:', error);
    } finally {
      setIsLoadingFileInfo(false);
    }
  }, [onChange, onFileInfoFetched]);

  // 执行单个文件上传（用于手动上传）
  const executeFileUpload = useCallback(async (file: RcFile): Promise<string> => {
    const finalBusinessType = getBusinessType ? getBusinessType(file) : businessType;
    
    try {
      // 1. 获取上传凭证
      const response = await generateUploadUrl({
        business_type: finalBusinessType,
        filename: file.name,
        custom_prefix: customPrefix || undefined,
      });

      if (response.code !== 0 || !response.data) {
        throw new Error(response.message || '获取上传凭证失败');
      }

      const { upload_url, form_data, file_url } = response.data;

      // 2. 构建 FormData
      const formData = new FormData();
      Object.keys(form_data).forEach((key) => {
        formData.append(key, form_data[key]);
      });
      formData.append('file', file);

      // 3. 上传到 OSS
      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error(`上传失败: ${uploadResponse.statusText}`);
      }

      return file_url;
    } catch (error: any) {
      // 如果直传失败，降级使用服务端中转
      console.warn('直传失败，降级使用服务端中转:', error);
      const response = await uploadFile(file, {
        business_type: finalBusinessType,
        custom_prefix: customPrefix || undefined,
      });
      if (response.code === 0 && response.data) {
        return response.data.file_url;
      }
      throw new Error(response.message || '上传失败');
    }
  }, [businessType, getBusinessType, customPrefix]);

  // 暴露手动上传接口
  useEffect(() => {
    if (uploadTriggerRef) {
      uploadTriggerRef.current = {
        // 上传所有待上传的文件
        uploadAll: async () => {
          const success: string[] = [];
          const failed: Array<{ file: File; error: Error }> = [];
          
          const uploads = Array.from(pendingUploads.entries());
          
          for (const [uid, file] of uploads) {
            try {
              // 更新状态为 uploading
              setFileList(prev => prev.map(f => 
                f.uid === uid ? { ...f, status: 'uploading', percent: 0 } : f
              ));
              
              const fileUrl = await executeFileUpload(file);
              
              // 更新为成功
              setFileList(prev => {
                const newList = prev.map(f => 
                  f.uid === uid ? { ...f, status: 'done' as const, url: fileUrl, percent: 100 } : f
                );
                const urls = newList.map(f => f.url).filter((url): url is string => !!url);
                onChange?.(urls);
                return newList;
              });
              
              // 从待上传队列移除
              setPendingUploads(prev => {
                const newMap = new Map(prev);
                newMap.delete(uid);
                return newMap;
              });
              
              success.push(fileUrl);
              onUploadSuccess?.({ uid, name: file.name, status: 'done', url: fileUrl }, fileUrl);
            } catch (error: any) {
              // 更新为失败
              setFileList(prev => prev.map(f => 
                f.uid === uid ? { ...f, status: 'error' as const } : f
              ));
              
              failed.push({ file, error });
              onUploadError?.({ uid, name: file.name, status: 'error' }, error);
            }
          }
          
          return { success, failed };
        },
        
        // 上传指定文件
        uploadFile: async (file: File) => {
          const fileUrl = await executeFileUpload(file as RcFile);
          return fileUrl;
        },
      };
    }
  }, [pendingUploads, executeFileUpload, onChange, onUploadSuccess, onUploadError, uploadTriggerRef]);

  // 创建上传处理函数
  const createUploadHandler = useCallback(
    (setFileListFn: React.Dispatch<React.SetStateAction<UploadFile[]>>) => {
      return (options: any) => {
        const { file, onSuccess, onError, onProgress } = options;
        const fileObj = file as RcFile;

        // 确定业务类型（优先使用回调函数）
        const finalBusinessType = getBusinessType ? getBusinessType(fileObj) : businessType;

        // 立即将文件添加到列表
        const newFile: UploadFile = {
          uid: fileObj.uid,
          name: fileObj.name,
          status: autoUpload ? 'uploading' : undefined, // autoUpload=false 时不设置状态（待上传）
          percent: autoUpload ? 0 : undefined,
          originFileObj: fileObj,
        };
        
        setFileListFn((prev) => {
          const newList = [...prev, newFile];
          if (maxCount && newList.length > maxCount) {
            return prev;
          }
          return newList;
        });

        // 如果不自动上传，加入待上传队列
        if (!autoUpload) {
          setPendingUploads(prev => {
            const newMap = new Map(prev);
            newMap.set(fileObj.uid, fileObj);
            return newMap;
          });
          // 标记为成功（实际未上传，只是显示文件）
          onSuccess?.({ url: '' }, fileObj);
          return;
        }

        // 异步处理上传
        (async () => {
          try {
            // 1. 获取上传凭证
            const response = await generateUploadUrl({
              business_type: finalBusinessType,
              filename: fileObj.name,
              custom_prefix: customPrefix || undefined,
            });

            if (response.code !== 0 || !response.data) {
              throw new Error(response.message || '获取上传凭证失败');
            }

            const { upload_url, form_data, file_url } = response.data;

            // 2. 构建 FormData
            const formData = new FormData();
            Object.keys(form_data).forEach((key) => {
              formData.append(key, form_data[key]);
            });
            formData.append('file', fileObj);

            // 3. 上传到 OSS（使用 XMLHttpRequest 以支持进度）
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
              if (e.lengthComputable) {
                const percent = Math.round((e.loaded * 100) / e.total);
                setFileListFn((prev) =>
                  prev.map((item) => {
                    if (item.uid === fileObj.uid) {
                      return { ...item, percent, status: 'uploading' };
                    }
                    return item;
                  })
                );
                onProgress?.({ percent });
                onUploadProgress?.(
                  {
                    uid: fileObj.uid,
                    name: fileObj.name,
                    status: 'uploading',
                    percent,
                  } as UploadFile,
                  percent
                );
              }
            });

            xhr.addEventListener('load', () => {
              if (xhr.status >= 200 && xhr.status < 300) {
                const completedFile: UploadFile = {
                  uid: fileObj.uid,
                  name: fileObj.name,
                  status: 'done',
                  url: file_url,
                  percent: 100,
                };
                
                // 保存文件信息（大小）
                setFileInfoMap((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(file_url, {
                    size: fileObj.size,
                    originalName: fileObj.name,
                  });
                  return newMap;
                });
                
                // 如果是视频文件，生成预览缩略图
                if (isVideoFile(completedFile)) {
                  generateVideoThumbnail(file_url).then((thumbnail) => {
                    if (thumbnail) {
                      setFileListFn((prev) => {
                        const newList = prev.map((item) => {
                          if (item.uid === fileObj.uid) {
                            return { ...item, thumbUrl: thumbnail };
                          }
                          return item;
                        });
                        return newList;
                      });
                    }
                  });
                }
                
                setFileListFn((prev) => {
                  const newList = prev.map((item) => {
                    if (item.uid === fileObj.uid) {
                      return completedFile;
                    }
                    return item;
                  });
                  // 触发 onChange
                  const urls = newList.map(f => f.url).filter((url): url is string => !!url);
                  onChange?.(urls);
                  return newList;
                });
                onSuccess?.({ url: file_url }, fileObj);
                onUploadSuccess?.(completedFile, file_url);
              } else {
                throw new Error(`上传失败: ${xhr.statusText}`);
              }
            });

            xhr.addEventListener('error', () => {
              throw new Error('上传失败：网络错误');
            });

            xhr.open('POST', upload_url);
            xhr.send(formData);
          } catch (error: any) {
            // 如果直传失败，降级使用服务端中转
            console.warn('直传失败，降级使用服务端中转:', error);
            try {
              const response = await uploadFile(fileObj, {
                business_type: finalBusinessType,
                custom_prefix: customPrefix || undefined,
              });
              if (response.code === 0 && response.data) {
                const fileUrl = response.data.file_url;
                const completedFile: UploadFile = {
                  uid: fileObj.uid,
                  name: fileObj.name,
                  status: 'done',
                  url: fileUrl,
                  percent: 100,
                };
                
                // 保存文件信息（大小）
                setFileInfoMap((prev) => {
                  const newMap = new Map(prev);
                  newMap.set(fileUrl, {
                    size: fileObj.size,
                    originalName: fileObj.name,
                  });
                  return newMap;
                });
                
                // 如果是视频文件，生成预览缩略图
                if (isVideoFile(completedFile)) {
                  generateVideoThumbnail(fileUrl).then((thumbnail) => {
                    if (thumbnail) {
                      setFileListFn((prev) => {
                        const newList = prev.map((item) => {
                          if (item.uid === fileObj.uid) {
                            return { ...item, thumbUrl: thumbnail };
                          }
                          return item;
                        });
                        return newList;
                      });
                    }
                  });
                }
                
                setFileListFn((prev) => {
                  const newList = prev.map((item) => {
                    if (item.uid === fileObj.uid) {
                      return completedFile;
                    }
                    return item;
                  });
                  // 触发 onChange
                  const urls = newList.map(f => f.url).filter((url): url is string => !!url);
                  onChange?.(urls);
                  return newList;
                });
                onSuccess?.({ url: fileUrl }, fileObj);
                onUploadSuccess?.(completedFile, fileUrl);
              } else {
                throw new Error(response.message || '上传失败');
              }
            } catch (fallbackError: any) {
              const errorFile: UploadFile = {
                uid: fileObj.uid,
                name: fileObj.name,
                status: 'error',
              };
              setFileListFn((prev) =>
                prev.map((item) => {
                  if (item.uid === fileObj.uid) {
                    return errorFile;
                  }
                  return item;
                })
              );
              onError?.(fallbackError);
              onUploadError?.(errorFile, fallbackError);
            }
          }
        })();
      };
    },
    [businessType, getBusinessType, customPrefix, maxCount, onChange, onUploadError, onUploadProgress, onUploadSuccess, autoUpload]
  );

  // 文件上传前验证
  const beforeUpload = (file: RcFile): boolean => {
    // 文件类型验证
    if (accept || allowedExtensions) {
      const isValid = validateFileType(file, accept, allowedExtensions);
      if (!isValid) {
        const allowed = allowedExtensions?.join('、') || accept;
        message.error(`只支持 ${allowed} 格式的文件`);
        return false;
      }
    }

    // 文件大小验证
    if (maxSize) {
      const isLtMax = file.size / 1024 / 1024 <= maxSize;
      if (!isLtMax) {
        message.error(`文件大小不能超过 ${maxSize}MB`);
        return false;
      }
    }

    return true;
  };

  // 处理文件删除
  const handleRemove = (file: UploadFile): boolean => {
    if (customOnRemove) {
      const result = customOnRemove(file);
      if (result === false) {
        return false;
      }
    }

    setFileList((prev) => {
      const newList = prev.filter((item) => item.uid !== file.uid);
      const urls = newList.map(f => f.url).filter((url): url is string => !!url);
      onChange?.(urls);
      return newList;
    });
    return true;
  };

  // 处理文件预览
  const handlePreview = (file: UploadFile): void => {
    if (customOnPreview) {
      customOnPreview(file);
      return;
    }

    if (!showPreview || !file.url) return;

    if (isImageFile(file)) {
      setPreviewImage(file.url);
      setIsPreviewModalOpen(true);
    } else if (isVideoFile(file)) {
      setPreviewVideo(file.url);
      setIsPreviewModalOpen(true);
    } else {
      // 其他文件类型，直接打开
      window.open(file.url, '_blank');
    }
  };

  // 渲染上传按钮
  const renderUploadButton = () => {
    // 预览模式下不显示上传按钮
    if (mode === 'preview') {
      return null;
    }

    // 已达最大数量时不显示上传按钮（picture-card / picture-list / text 统一）
    if (fileList.length >= (maxCount || 1)) {
      return null;
    }

    const {
      text,
      icon = <UploadOutlined />,
      type = 'default',
      size = 'middle',
      style,
      className,
    } = buttonConfig;

    if (normalizedListType === 'picture-card') {
      return (
        <div>
          <PlusOutlined />
          <div style={{ marginTop: 8 }}>{text || '上传'}</div>
        </div>
      );
    }

    return (
      <Button icon={icon} type={type} size={size} style={style} className={className}>
        {text || '上传文件'}
      </Button>
    );
  };

  // 渲染文件图标（用于非图片/视频文件）
  const renderFileIcon = (file: UploadFile) => {
    if (isImageFile(file) || isVideoFile(file)) {
      return null; // 图片和视频使用预览图
    }
    
    const extension = getFileExtension(file);
    const fileType = extension || 'file';
    
    // 使用 react-file-icon 根据文件类型显示图标
    return (
      <div style={{ width: 48, height: 48 }}>
        <FileIcon
          extension={fileType}
          {...(defaultStyles as any)[fileType]}
          labelColor="#1890ff"
        />
      </div>
    );
  };

  // 自定义文件列表项渲染
  const itemRender = (originNode: React.ReactElement, file: UploadFile) => {
    const fileInfo = file.url ? fileInfoMap.get(file.url) : null;
    const fileSize = fileInfo?.size || (file as any).size || 0;
    const originalName = fileInfo?.originalName || file.name;
    const extension = getFileExtension(file);
    // 判断是否在待上传队列中
    const isPending = pendingUploads.has(file.uid);
    
    // picture-list 模式：列表式展示，显示更多信息
    if (normalizedListType === 'picture-list') {
      // 克隆 originNode 并修改其内容，保留操作按钮
      const nodeProps = (originNode.props || {}) as Record<string, any>;
      const children = React.Children.toArray(nodeProps.children || []);
      
      // 查找操作按钮容器（通常是一个包含预览和删除按钮的 div）
      let actionsElement: React.ReactNode = null;
      const contentChildren: React.ReactNode[] = [];
      
      React.Children.forEach(children, (child: any) => {
        if (React.isValidElement(child)) {
          const childProps = (child.props || {}) as Record<string, any>;
          // 查找包含操作按钮的元素（通常类名包含 'actions' 或包含按钮元素）
          if (childProps.className?.includes('ant-upload-list-item-actions') || 
              (typeof childProps.children === 'object' && 
               React.Children.toArray(childProps.children).some((c: any) => {
                 const cProps = (c?.props || {}) as Record<string, any>;
                 return cProps.className?.includes('anticon-delete') || 
                        cProps.className?.includes('anticon-eye');
               }))) {
            actionsElement = child;
          } else {
            contentChildren.push(child);
          }
        }
      });
      
      // 如果没找到，尝试从整个 originNode 结构中提取
      if (!actionsElement) {
        // 查找所有可能的操作按钮
        const findActions = (node: any): React.ReactNode => {
          if (!node || !React.isValidElement(node)) return null;
          const nodeProps = node.props as Record<string, any> | undefined;
          if (!nodeProps) return null;
          if (nodeProps.className?.includes('ant-upload-list-item-actions')) {
            return node;
          }
          if (nodeProps.children) {
            const childrenArray = React.Children.toArray(nodeProps.children);
            for (const child of childrenArray) {
              const found = findActions(child);
              if (found) return found;
            }
          }
          return null;
        };
        actionsElement = findActions(originNode);
      }
      
      return (
        <button
          type="button"
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            padding: '8px 0',
            borderBottom: '1px solid #f0f0f0',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            font: 'inherit',
            textAlign: 'left',
          }}
          onClick={(e) => {
            // 如果点击的不是操作按钮区域，触发预览
            const target = e.target as HTMLElement;
            if (!target.closest('.ant-upload-list-item-actions')) {
              handlePreview(file);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              const target = e.target as HTMLElement;
              if (!target.closest('.ant-upload-list-item-actions')) {
                e.preventDefault();
                handlePreview(file);
              }
            }
          }}
        >
          <div style={{ 
            marginRight: 12, 
            flexShrink: 0, 
            width: 48, 
            height: 48, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            borderRadius: 4,
            overflow: 'hidden',
            backgroundColor: '#fafafa',
          }}>
            {isImageFile(file) || isVideoFile(file) ? (
              <Image
                src={file.thumbUrl || file.url}
                alt={file.name}
                width={48}
                height={48}
                style={{ objectFit: 'cover', borderRadius: 4 }}
                preview={false}
              />
            ) : (
              <div style={{ 
                width: 48, 
                height: 48, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                overflow: 'visible',
              }}>
                <div style={{
                  transform: 'scale(0.75)',
                  transformOrigin: 'center',
                  width: 64,
                  height: 64,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <FileIcon
                    extension={extension || 'file'}
                    {...(defaultStyles as any)[extension || 'file']}
                    labelColor="#1890ff"
                  />
                </div>
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {originalName}
            </div>
            {file.status === 'uploading' ? (
              // 上传中：显示进度条
              <div style={{ marginTop: 4 }}>
                <Progress 
                  percent={Math.round(file.percent || 0)} 
                  size="small" 
                  status="active"
                  strokeColor="#1890ff"
                />
              </div>
            ) : (
              // 其他状态：显示文件信息
              <div style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.45)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {fileSize > 0 && <span>大小: {formatFileSize(fileSize)}</span>}
                {extension && <span>类型: {extension.toUpperCase()}</span>}
                {file.status === 'done' && <span style={{ color: '#52c41a' }}>上传成功</span>}
                {file.status === 'error' && <span style={{ color: '#ff4d4f' }}>上传失败</span>}
                {!file.status && isPending && <span style={{ color: '#1890ff' }}>待上传</span>}
              </div>
            )}
          </div>
          {/* 保留操作按钮 */}
          {actionsElement && (
            <div 
              style={{ marginLeft: 8, flexShrink: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {actionsElement}
            </div>
          )}
        </button>
      );
    }

    // picture-card 模式：使用 Tooltip 显示额外信息，保持原有 hover 效果
    if (normalizedListType === 'picture-card') {
      const tooltipContent = (
        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{originalName}</div>
          {fileSize > 0 && <div>大小: {formatFileSize(fileSize)}</div>}
          {extension && <div>类型: {extension.toUpperCase()}</div>}
        </div>
      );
      
      return (
        <Tooltip title={tooltipContent} placement="top">
          {originNode}
        </Tooltip>
      );
    }
    
    // text 模式：传统文字列表，前面有小 icon
    if (normalizedListType === 'text') {
      // 从 originNode 中提取操作按钮
      const nodeProps = (originNode.props || {}) as Record<string, any>;
      const children = React.Children.toArray(nodeProps.children || []);
      
      let actionsElement: React.ReactNode = null;
      
      React.Children.forEach(children, (child: any) => {
        if (React.isValidElement(child)) {
          const childProps = (child.props || {}) as Record<string, any>;
          if (childProps.className?.includes('ant-upload-list-item-actions') || 
              (typeof childProps.children === 'object' && 
               React.Children.toArray(childProps.children).some((c: any) => {
                 const cProps = (c?.props || {}) as Record<string, any>;
                 return cProps.className?.includes('anticon-delete') || 
                        cProps.className?.includes('anticon-eye');
               }))) {
            actionsElement = child;
          }
        }
      });
      
      // 如果没找到，尝试从整个 originNode 结构中提取
      if (!actionsElement) {
        const findActions = (node: any): React.ReactNode => {
          if (!node || !React.isValidElement(node)) return null;
          const nodeProps = node.props as Record<string, any> | undefined;
          if (!nodeProps) return null;
          if (nodeProps.className?.includes('ant-upload-list-item-actions')) {
            return node;
          }
          if (nodeProps.children) {
            const childrenArray = React.Children.toArray(nodeProps.children);
            for (const child of childrenArray) {
              const found = findActions(child);
              if (found) return found;
            }
          }
          return null;
        };
        actionsElement = findActions(originNode);
      }
      
      // 根据文件类型选择图标
      let fileIcon: React.ReactNode;
      if (isImageFile(file)) {
        fileIcon = <FileImageOutlined style={{ fontSize: 14, color: '#1890ff' }} />;
      } else if (isVideoFile(file)) {
        fileIcon = <PlayCircleOutlined style={{ fontSize: 14, color: '#1890ff' }} />;
      } else {
        fileIcon = <FileOutlined style={{ fontSize: 14, color: '#1890ff' }} />;
      }
      
      const truncatedName = truncateFileName(originalName);
      const tooltipContent = (
        <div style={{ fontSize: 12, lineHeight: 1.6 }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{originalName}</div>
          {fileSize > 0 && <div>大小: {formatFileSize(fileSize)}</div>}
          {extension && <div>类型: {extension.toUpperCase()}</div>}
        </div>
      );
      
      return (
        <Tooltip title={tooltipContent} placement="top">
          <button
            type="button"
            className="file-uploader-text-item"
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: 4,
              transition: 'background-color 0.2s',
              border: 'none',
              background: 'none',
              font: 'inherit',
              textAlign: 'left',
            }}
            onClick={(e) => {
              const target = e.target as HTMLElement;
              if (!target.closest('.ant-upload-list-item-actions')) {
                handlePreview(file);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                const target = e.target as HTMLElement;
                if (!target.closest('.ant-upload-list-item-actions')) {
                  e.preventDefault();
                  handlePreview(file);
                }
              }
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span style={{ marginRight: 8, flexShrink: 0, display: 'flex', alignItems: 'center', lineHeight: '22px' }}>
              {fileIcon}
            </span>
            <span 
              style={{ 
                flex: 1, 
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: '22px',
                color: '#1890ff',
                transition: 'color 0.2s',
              }}
              title={originalName}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#40a9ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#1890ff';
              }}
            >
              {truncatedName}
            </span>
            {actionsElement && (
              <span
                style={{ marginLeft: 8, flexShrink: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                {actionsElement}
              </span>
            )}
          </button>
        </Tooltip>
      );
    }

    return originNode;
  };

  // 预览模式下的配置
  const isPreviewMode = mode === 'preview';
  const effectiveShowRemove = isPreviewMode ? false : showRemove;
  const effectiveDisabled = isPreviewMode ? true : disabled;
  const effectiveShowPreview = showPreview; // 预览模式下仍然可以预览

  // Upload 组件配置
  const uploadConfig: UploadProps = {
    customRequest: isPreviewMode ? undefined : createUploadHandler(setFileList),
    fileList,
    onRemove: effectiveShowRemove ? handleRemove : undefined,
    onPreview: effectiveShowPreview ? handlePreview : undefined,
    beforeUpload: isPreviewMode ? () => false : beforeUpload, // 预览模式下阻止上传
    maxCount,
    multiple: maxCount ? maxCount > 1 : false,
    listType: normalizedListType === 'picture-list' ? 'picture' : normalizedListType,
    disabled: effectiveDisabled,
    accept: finalAccept, // 使用自动生成的 accept
    progress: {
      strokeWidth: 4,
      showInfo: true,
      format: (percent) => `${Math.round(percent || 0)}%`,
    },
    iconRender: renderFileIcon,
    itemRender: itemRender,
    ...uploadProps,
  };

  // 如果启用图片裁剪且是图片类型
  const shouldUseCrop =
    cropConfig?.enabled &&
    businessType === 'image' &&
    normalizedListType === 'picture-card';

  const uploadComponent = <Upload {...uploadConfig}>{renderUploadButton()}</Upload>;

  return (
    <>
      {shouldUseCrop ? (
        <ImgCrop
          aspect={cropConfig.aspect}
          quality={cropConfig.quality}
          zoom={cropConfig.zoom}
          rotate={cropConfig.rotate}
          modalTitle={cropConfig.modalTitle}
          modalOk={cropConfig.modalOk}
          modalCancel={cropConfig.modalCancel}
        >
          {uploadComponent}
        </ImgCrop>
      ) : (
        uploadComponent
      )}

      {extra && <div style={{ marginTop: 8, color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>{extra}</div>}

      {/* 图片预览模态框 */}
      <Modal
        open={isPreviewModalOpen && !!previewImage && !previewVideo}
        footer={null}
        onCancel={() => {
          setIsPreviewModalOpen(false);
          setPreviewImage('');
          setPreviewVideo('');
        }}
        width={800}
        centered
      >
        <Image src={previewImage} alt="预览" style={{ width: '100%' }} preview={false} />
      </Modal>

      {/* 视频预览模态框 */}
      <Modal
        open={isPreviewModalOpen && !!previewVideo}
        footer={null}
        onCancel={() => {
          setIsPreviewModalOpen(false);
          setPreviewImage('');
          setPreviewVideo('');
        }}
        width={800}
        centered
      >
        <video src={previewVideo} controls style={{ width: '100%' }} autoPlay={false}>
          <track kind="captions" />
          您的浏览器不支持视频播放
        </video>
      </Modal>
    </>
  );
};

export default ProFileUpload;
